import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import VoiceInputButton from './VoiceInputButton.jsx';

const appendText = (cur, add) => (cur && cur.trim() ? `${cur.trim()} ${add}` : add);

/**
 * Modal for creating or editing a follow-up.
 *
 * Props:
 *   initial — { id?, title, description, due_date, urgency, customer_id, note_id, touch_type }
 *             When id is present, the modal edits the existing row (PUT).
 *             When id is absent, creates a new row (POST).
 *   onClose — close the modal
 *   onSaved(row) — called with the saved row on success
 */
export default function FollowupEditor({ initial = {}, onClose, onSaved }) {
  const qc = useQueryClient();
  const isEdit = Boolean(initial.id);

  const customers = useQuery({
    queryKey: ['customers', 'lookup'],
    queryFn: () => api.get('/customers').then(r => r.data.data),
  });

  const today = new Date();
  const defaultDue = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    title: initial.title || '',
    description: initial.description || '',
    due_date: initial.due_date ? String(initial.due_date).slice(0, 10) : defaultDue,
    urgency: initial.urgency || 'medium',
    customer_id: initial.customer_id || '',
    customer_name_hint: initial.customer_id ? '' : (initial.customer_name_hint || ''),
    note_id: initial.note_id || null,
    touch_type: initial.touch_type || 'note',
  });
  const [customerSearch, setCustomerSearch] = useState('');

  const save = useMutation({
    mutationFn: () => {
      const custId = form.customer_id ? parseInt(form.customer_id, 10) : null;
      const payload = {
        title: form.title,
        description: form.description || null,
        due_date: form.due_date,
        urgency: form.urgency,
        customer_id: custId,
        ...(!custId && form.customer_name_hint ? { customer_name_hint: form.customer_name_hint } : {}),
        note_id: form.note_id || null,
        touch_type: form.touch_type || null,
      };
      return isEdit
        ? api.put(`/followups/${initial.id}`, payload).then(r => r.data.data)
        : api.post('/followups', payload).then(r => r.data.data);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['followups'] });
      qc.invalidateQueries({ queryKey: ['customer-activity'] });
      onSaved?.(row);
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{isEdit ? 'Edit follow-up' : 'New follow-up'}</h3>

        <div>
          <label htmlFor="fu-title" className="label">Title *</label>
          <div className="flex gap-2 items-center">
            <input id="fu-title" className="input flex-1" value={form.title}
                   onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
            <VoiceInputButton title="Dictate title"
              onTranscript={t => setForm(f => ({ ...f, title: appendText(f.title, t) }))} />
          </div>
        </div>
        <div>
          <label htmlFor="fu-customer" className="label">Customer</label>
          {form.customer_name_hint && !form.customer_id && (
            <div className="text-xs text-amber-800 mb-1">
              📝 {form.customer_name_hint}{' '}
              <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">not linked</span>
              {' — '}
              <button type="button" className="underline" onClick={() => setForm(f => ({ ...f, customer_name_hint: '' }))}>
                clear
              </button>
            </div>
          )}
          <input className="input mb-1" placeholder="Search customer…"
                 value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
          <select id="fu-customer" className="input" value={form.customer_id}
                  onChange={e => setForm({ ...form, customer_id: e.target.value, customer_name_hint: e.target.value ? '' : form.customer_name_hint })}>
            <option value="">— General task (no customer) —</option>
            {(customerSearch
              ? customers.data?.filter(c => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
              : customers.data)?.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.city ? ` — ${[c.city, c.state].filter(Boolean).join(', ')}` : ''}
                </option>
              ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="fu-due" className="label">Due *</label>
            <input id="fu-due" className="input" type="date" value={form.due_date}
                   onChange={e => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <label htmlFor="fu-urgency" className="label">Urgency</label>
            <select id="fu-urgency" className="input" value={form.urgency}
                    onChange={e => setForm({ ...form, urgency: e.target.value })}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="fu-touch-type" className="label">Touch type</label>
          <select id="fu-touch-type" className="input" value={form.touch_type}
                  onChange={e => setForm({ ...form, touch_type: e.target.value })}>
            <option value="note">Note</option>
            <option value="call">Call</option>
            <option value="visit">Visit</option>
            <option value="email">Email</option>
            <option value="quote">Quote</option>
          </select>
        </div>
        <div>
          <label htmlFor="fu-notes" className="label">Notes</label>
          <div className="flex gap-2 items-start">
            <textarea id="fu-notes" className="input min-h-[60px] flex-1" value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      spellCheck={true} autoCorrect="on" autoCapitalize="sentences" />
            <VoiceInputButton title="Dictate notes"
              onTranscript={t => setForm(f => ({ ...f, description: appendText(f.description, t) }))} />
          </div>
        </div>

        {save.isError && (
          <div className="text-crimson text-sm">{save.error?.response?.data?.error || 'Save failed'}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary"
                  disabled={!form.title || !form.due_date || save.isPending}
                  onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
